import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ShieldCheck, FileText, AlertTriangle, ChevronRight, CheckCircle2, Clock, X } from 'lucide-react';

/**
 * Professional banner reminding sellers to verify their identity and fiscal profile.
 * Disappears once both: KYC is approved AND fiscal profile is configured.
 */
export function VerificationBanner() {
    const { user, role } = useAuth();
    const [status, setStatus] = useState(null); // null=loading, false=hidden, object=show
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!user || role !== 'user') { setStatus(false); return; }
        Promise.all([
            api.get('/me/kyc').catch(() => ({ data: null })),
            api.get('/me/fiscal-profile').catch(() => ({ data: null })),
        ]).then(([kycRes, fiscalRes]) => {
            const kycApproved = kycRes.data?.latest?.status === 'approved';
            const fiscalDone = !!(fiscalRes.data?.fiscal_regime);
            if (kycApproved && fiscalDone) {
                setStatus(false); // fully verified — hide banner
            } else {
                setStatus({ kycApproved, fiscalDone, fiscalRegime: fiscalRes.data?.fiscal_regime });
            }
        });
    }, [user, role]);

    if (!status || dismissed) return null;

    const { kycApproved, fiscalDone, fiscalRegime } = status;
    const isSinRfc = fiscalRegime === 'sin_rfc';

    return (
        <div className="rounded-2xl overflow-hidden shadow-lg border border-amber-200/60 bg-white" data-testid="verification-banner">
            {/* Header strip */}
            <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-red-900 px-5 py-4 text-white">
                <button
                    onClick={() => setDismissed(true)}
                    className="absolute top-3 right-3 text-white/50 hover:text-white/90 transition-colors"
                    aria-label="Cerrar"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="shrink-0 h-10 w-10 rounded-xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                        <div className="font-bold text-sm tracking-tight">¡Verifica tu cuenta para vender con menores retenciones fiscales!</div>
                        <div className="text-[11px] text-white/70 mt-0.5">Sin verificación, el SAT retiene hasta <span className="text-amber-400 font-semibold">20% ISR + 16% IVA</span> de cada venta.</div>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 bg-gradient-to-b from-slate-50 to-white">
                {/* Tax comparison table */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-1">Sin verificar</div>
                        <div className="text-2xl font-black text-red-600">36%</div>
                        <div className="text-[10px] text-red-700 mt-0.5">ISR 20% + IVA 16%</div>
                        <div className="text-[10px] text-red-500 mt-1">Retención máxima del SAT</div>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold mb-1">Con RFC verificado</div>
                        <div className="text-2xl font-black text-emerald-600">10.5%</div>
                        <div className="text-[10px] text-emerald-700 mt-0.5">ISR 2.5% + IVA 8%</div>
                        <div className="text-[10px] text-emerald-500 mt-1">Retención mínima aplicable</div>
                    </div>
                </div>

                {/* Steps */}
                <div className="space-y-2 mb-4">
                    <Step
                        done={kycApproved}
                        icon={ShieldCheck}
                        title="Verificación de identidad"
                        desc={kycApproved ? 'Identidad verificada ✓' : 'Sube tu INE o pasaporte + selfie'}
                        accent="blue"
                    />
                    <Step
                        done={fiscalDone && !isSinRfc}
                        warning={isSinRfc}
                        icon={FileText}
                        title="Perfil fiscal"
                        desc={
                            isSinRfc
                                ? 'Tienes perfil sin RFC — registra tu RFC para pagar menos impuestos'
                                : fiscalDone
                                    ? 'Perfil fiscal configurado ✓'
                                    : 'Ingresa tu RFC y régimen fiscal (Persona Física o Moral)'
                        }
                        accent="amber"
                    />
                </div>

                {/* CTA */}
                <Link
                    to="/dashboard/kyc"
                    className="flex items-center justify-between w-full rounded-xl bg-gradient-to-r from-slate-900 to-red-800 text-white px-4 py-3 font-semibold text-sm hover:from-slate-800 hover:to-red-700 transition-all shadow-md"
                >
                    <span>Verificar mi cuenta ahora</span>
                    <ChevronRight className="h-4 w-4" />
                </Link>
                <p className="text-[10px] text-muted-foreground text-center mt-2">
                    Este aviso desaparece al completar ambos pasos · Regulación SAT México
                </p>
            </div>
        </div>
    );
}

function Step({ done, warning, icon: Icon, title, desc, accent }) {
    const colors = {
        blue: { ring: 'border-blue-200 bg-blue-50', icon: 'text-blue-600 bg-blue-100', text: 'text-blue-700' },
        amber: { ring: 'border-amber-200 bg-amber-50', icon: 'text-amber-600 bg-amber-100', text: 'text-amber-700' },
    };
    const c = colors[accent] || colors.blue;

    return (
        <div className={`flex items-start gap-3 rounded-xl border p-3 ${done ? 'border-emerald-200 bg-emerald-50' : warning ? 'border-amber-200 bg-amber-50' : c.ring}`}>
            <div className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${done ? 'bg-emerald-100' : warning ? 'bg-amber-100' : c.icon}`}>
                {done
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    : warning
                        ? <AlertTriangle className="h-4 w-4 text-amber-600" />
                        : <Clock className="h-4 w-4 text-slate-500" />
                }
            </div>
            <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold ${done ? 'text-emerald-700' : warning ? 'text-amber-700' : 'text-slate-700'}`}>{title}</div>
                <div className={`text-[11px] mt-0.5 ${done ? 'text-emerald-600' : warning ? 'text-amber-600' : 'text-slate-500'}`}>{desc}</div>
            </div>
            <div className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${done ? 'bg-emerald-200 text-emerald-800' : warning ? 'bg-amber-200 text-amber-800' : 'bg-slate-200 text-slate-600'}`}>
                {done ? 'Listo' : warning ? 'Atención' : 'Pendiente'}
            </div>
        </div>
    );
}
